'use strict';
import { AppConfigAPI } from '@models/types/appconfig.d';
import { AppConfigProvider } from '@root/app/app.config.provider';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as SPREAD_CONFIG_LIQSRC from '@components/model-structure/liquidity-sources/liquidity-sources.spread.config';
import { Observable, of } from 'rxjs';
import { defaultIfEmpty } from 'rxjs/operators';
import { capitalizeString } from '@shared/helpers/capitalizeString';
import { trimString } from '@helpers/trimString';

import * as GC from '@grapecity/spread-sheets';
import { EquationsTreeNode } from '@models/equations-tree-node';
import { EquationsService } from '../equations/equations.service';
import { TrackingService } from '../tracking/tracking.service';
import { LiquiditySource } from '@interfaces/liquidity-source';
import { objectsAreEqual } from '@helpers/objects-are-equal';
import { clone, cloneDeep } from 'lodash-es';

const LIQSRC_PATH = '/liquiditysources';
const SPREADCONFIG = SPREAD_CONFIG_LIQSRC.SpreadConfig;

@Injectable({ providedIn: 'root' })
export class LiquiditySourcesService {
  static preparedObject: any = {};
  public sourceIdentifiers = [];
  public setOfSourcesToInsert = new Set<number>();
  public setOfSourcesToUpdate = new Set<number>();
  public setOfSourcesToDelete = new Set<number>();
  public myUserId: number = null;
  public onLoadTimestamp: number = null;
  public onEditTimestamp: number = null;
  private environment: AppConfigAPI;

  get sourcesToInsert(): number[] {
    return Array.from(this.setOfSourcesToInsert)
      .filter(elem => elem) // Excludes null and other falsy values
      .sort();
  }
  get sourcesToUpdate(): number[] {
    return Array.from(this.setOfSourcesToUpdate)
      .filter(elem => elem) // Excludes null and other falsy values
      .sort();
  }
  get sourcesToDelete(): number[] {
    return Array.from(this.setOfSourcesToDelete)
      .filter(elem => elem) // Excludes null and other falsy values
      .sort();
  }

  constructor(
    private http: HttpClient,
    private eqService: EquationsService,
    private tracking: TrackingService,
    private appConfig: AppConfigProvider
  ) {
    this.appConfig.environment.subscribe(env => {
      this.environment = env.API;
    });
  }

  // Gets all the Liquidity Sources
  public getAllLiquiditySources(): Promise<object[]> {
    const url = `${this.environment.SOURCESAPI}/api${LIQSRC_PATH}`;
    return this.http
      .get<object[]>(url)
      .pipe(defaultIfEmpty([]))
      .toPromise()
      .catch(() => []);
  }

  public async getAllLiquiditySourcesPeriodEquations() {
    const sources = await this.getAllLiquiditySources();
    const listOfIds = sources.map(source => source['source_sid']);
    const allEquations = await this.eqService.getAllPeriodEquations('LiquiditySourcesPeriodEquations', listOfIds);
    const hasTemp = allEquations.some(equation => equation['isFromTracking']);
    this.tracking.updateModelDataChangesFlags('liquiditySources', hasTemp, 'getAllLiquiditySourcesPeriodEquations');
    return allEquations;
  }

  // Gets the data of one specific liquidity source
  public getLiquiditySource(source_sid: number): Observable<object[]> {
    const url = `${this.environment.SOURCESAPI}/api${LIQSRC_PATH}/${source_sid}`;
    const liqSource$ = this.http.get<object[]>(url) || of([]);
    return liqSource$;
  }

  public addSource_sid(rows) {
    const processedRows = rows.map(row => {
      if (row['source_sid'] !== 0) {
        // It isn't a new insertion
        const identifier = this.sourceIdentifiers.find(elem => elem.mnemonic === row['mnemonic']) || false;
        if (identifier && identifier['source_sid']) {
          row['source_sid'] = identifier.source_sid;
        }
      }
      return row;
    });
    
    return processedRows;
  }

  /**
   * Saves the period equations in the database
   * @param {object[]} spreadContent
   * @param {number} resourceId
   * @memberof LiquiditySourcesService
   */
  public async savePeriodEquations(spreadContent: object[], resourceId: number) {
    return await this.eqService.savePeriodEquations('LiquiditySourcesPeriodEquations', spreadContent, resourceId);
  }

  // Builds the final object to be inserted in the DB
  private buildObjectToInsertInDatabase(row): LiquiditySource {
    // Convert empty strings to null for numeric fields
    const processedRow = {
      ...row,
      user_id: -1,
    };
    
    // Convert empty balance_sheet_add_factor to null
    if (processedRow.balance_sheet_add_factor === '' || processedRow.balance_sheet_add_factor === undefined) {
      processedRow.balance_sheet_add_factor = null;
    }
    
    // Convert empty balance_sheet_account_mnemonic to null if needed
    if (processedRow.balance_sheet_account_mnemonic === '') {
      processedRow.balance_sheet_account_mnemonic = null;
    }
    
    return processedRow as LiquiditySource;
  }

  public incrementSourceSid(mnemonic) {
    const nextSourceSid = this.getHighestSource_sid() + 1;
    this.sourceIdentifiers.push({
      mnemonic: mnemonic,
      source_sid: nextSourceSid,
    });
    return nextSourceSid;
  }

  public getHighestSource_sid() {
    let numbers;
    if (
      this.sourceIdentifiers &&
      this.sourceIdentifiers.hasOwnProperty('length') &&
      this.sourceIdentifiers.length > 0 &&
      this.sourceIdentifiers[0]['source_sid']
    ) {
      numbers = this.sourceIdentifiers.map(identifier => identifier['source_sid']);
    } else {
      numbers = [0];
    }
    const highestSourceSid = numbers ? Math.max(...numbers) : 0;
    return highestSourceSid;
  }

  // Update
  // =============
  public performUpdateOperation(spreadContent: LiquiditySource[], spreadContentOriginal: LiquiditySource[]) {
    // --- filter only for those one that aren't inserts or deletes
    const sidsToUpdate = this.getOnlyUpdatedSourceSids(spreadContent, spreadContentOriginal) || [];
    if (sidsToUpdate.length > 0) {
      // Set Mapping for updates
      sidsToUpdate.forEach(sid => this.setOfSourcesToUpdate.add(sid));
      const rowsToUpdate = spreadContent.filter(row => this.sourcesToUpdate.includes(row['source_sid'])) || false;
      if (rowsToUpdate && rowsToUpdate.length > 0) {
        return this.updateLiquiditySources(rowsToUpdate);
      }
    }
    return of([]);
  }

  // Delete
  // =============
  public performDeleteOperation(spreadContent: LiquiditySource[], spreadContentOriginal: LiquiditySource[]) {
    this.updateDeletions(spreadContent, spreadContentOriginal);
    return this.deleteLiquiditySources();
  }

  public updateDeletions(spreadContent: LiquiditySource[], spreadContentOriginal: LiquiditySource[]) {
    // Delete all the source_sids that are not in the spreadContent array now. Exclude source_sid 0 (insertions)
    const key = 'source_sid';

    // Get the sids for deletion
    const originalSids = spreadContentOriginal.map(row => row[key]);
    const currentSidsExcludingInsertionsAndEmptyRows = spreadContent
      .filter(row => row[key] > 0 && !this.isEmptyDefaultOrBlankLiquiditySource(row))
      .map(row => row[key]);

    // sidsToDelete ==> Pick those sids that currently are not in the data, but they were in the original data
    const sidsToDelete = [...originalSids.filter(sid => !currentSidsExcludingInsertionsAndEmptyRows.includes(sid))];

    // Update the reference for deletions
    this.setOfSourcesToDelete.clear();
    sidsToDelete.forEach(sid => this.setOfSourcesToDelete.add(sid));
  }

  private deleteLiquiditySources(): Observable<LiquiditySource[]> {
    const initDelete = () => {
      this.deletePeriodEquationsTrackingItems();
      const url = `${this.environment.SOURCESAPI}/api${LIQSRC_PATH}/delete`;
      return this.http.post<object[]>(url, this.sourcesToDelete) as Observable<LiquiditySource[]>;
    };

    return this.sourcesToDelete.length > 0 ? initDelete() : of([]);
  }

  private deletePeriodEquationsTrackingItems() {
    // Bug fix 2276: Delete tracking record when the user has delete the exogenous line
    // Note: It can be done through the store procedure that handles the save, but is done
    // here to make each microservice handle his own domain independently
    // (tracking for tracking-related task, exogenous for exogenous lines period eq.)
    let success = false;
    this.sourcesToDelete.forEach((resId, index) => {
      const resourceId = resId;
      const trackingDeleted = this.eqService.deletePeriodEquationsTrackingItems('LiquiditySourcesPeriodEquations', resourceId, -1);
      trackingDeleted.then(deleted => {
        if (deleted) {
          if (index === this.sourcesToDelete.length - 1) {
            success = true;
            return success;
          }
        } else {
          console.error(`The period equations tracking item for liquidity source ${resourceId} cannot be deleted`);
        }
      });
    });
  }

  // Updates of existing Liquidity Sources (updates) and inactivate previous records
  private updateLiquiditySources(sourcesToUpdate): Observable<LiquiditySource[]> {
    // Add the user_id
    sourcesToUpdate.map(row => (row['user_id'] = -1) && row);
    const url = `${this.environment.SOURCESAPI}/api${LIQSRC_PATH}/update`;
    return this.http.post<object[]>(url, sourcesToUpdate) as Observable<LiquiditySource[]>;
  }

  // Insert
  // =============
  public performInsertOperation(spreadContent: LiquiditySource[]): Observable<LiquiditySource[]> {
    if (spreadContent) {
      const rowsToInsert: LiquiditySource[] = spreadContent
        // --- pick only those that are new insertions
        .filter((row: LiquiditySource) => {
          const isNewInsertion = row.source_sid <= 0 && row.mnemonic;
          return isNewInsertion;
        })
        .map(row => {
          const builtRow = this.buildObjectToInsertInDatabase(row);
          return builtRow;
        });
      
      if (rowsToInsert) {
        return this.insertLiquiditySources(rowsToInsert);
      }
    }
    return of([]);
  }

  private insertLiquiditySources(sourcesToInsert: LiquiditySource[]): Observable<LiquiditySource[]> {
    if (sourcesToInsert.length > 0) {
      sourcesToInsert.map(row => (row['user_id'] = -1) && row);
      const url = `${this.environment.SOURCESAPI}/api${LIQSRC_PATH}/insert`;
      return this.http.post<object[]>(url, sourcesToInsert) as Observable<LiquiditySource[]>;
    }
    return of([]);
  }

  public getOnlyUpdatedSourceSids(spreadContent, spreadContentOriginal) {
    const identifiersToUpdate = [...this.sourceIdentifiers]
      .filter(identifier => {
        const isUpdateCandidate =
          (identifier &&
            !this.sourcesToDelete.includes(identifier['source_sid']) &&
            !this.sourcesToInsert.includes(identifier['source_sid'])) ||
          false;

        if (isUpdateCandidate) {
          // We compare if this identifier has some modification
          const targetCurrent = (spreadContent.find(row => row['source_sid'] === identifier['source_sid']) as LiquiditySource) || false;

          const targetPrevious =
            (spreadContentOriginal.find(row => row['source_sid'] === identifier['source_sid']) as LiquiditySource) || false;

          // It's important to delete user_id from both objects before comparing
          delete targetCurrent['user_id'];
          delete targetPrevious['user_id'];

          if (targetCurrent && targetPrevious) {
            const hasBeenModified = !objectsAreEqual(targetCurrent, targetPrevious);
            return hasBeenModified;
          }
        }
        return false;
      })
      .map(identifier => identifier['source_sid']);

    return identifiersToUpdate;
  }

  /**
   * Checks if exists a previous temporary record.
   * If it does, returns the data. If it does not exist, returns false
   * @returns
   * @memberof LiquiditySourcesService
   */
  public async getTemporaryRecordForPeriodEquations(resourceEquationsPath: string, userId: number, resourceId: number): Promise<Object[]> {
    const url = `${this.environment.TRACKINGAPI}/api/tracking${resourceEquationsPath}?check_temp=1&resource_id=${resourceId}`;
    const temporaryRecord = await this.http
      .get<object[]>(url)
      .toPromise()
      .catch(() => null);
    return temporaryRecord;
  }

  /*
   **  TREE-related methods
   */
  public treePreBuild(apiData) {
    if (apiData) {
      const liqSrcTypes = {};
      apiData.forEach(row => {
        const type = capitalizeString(row['type']);
        if (this.isValidType(type)) {
          const key = row['name'];
          const value = row['mnemonic'];
          if (!liqSrcTypes[type]) {
            liqSrcTypes[type] = {};
          }
          liqSrcTypes[type][key] = value;
        } else {
          // Invalid Type
          console.error(`The item with type ${type} is not valid and has been ignored in the tree`);
        }
      });

      const newTree = { ...liqSrcTypes };
      return newTree;
    }
    return false;
  }

  public treeBuildNodes(obj: { [key: string]: any }, level: number): EquationsTreeNode[] {
    return Object.keys(obj)
      .sort()
      .reduce<EquationsTreeNode[]>((accumulator, key) => {
        const value = obj[key];
        const node = new EquationsTreeNode();
        node.name = key;

        if (value !== null) {
          if (typeof value === 'object') {
            node.children = this.treeBuildNodes(value, level + 1);
          } else {
            node.value = value;
          }
        }

        return accumulator.concat(node);
      }, []);
  }

  /*
   * Checks if the parameter is a liquidity source's valid type (case insensitive)
   */
  public isValidType(typeToCheck = null): boolean {
    return (
      typeToCheck &&
      typeof typeToCheck === 'string' &&
      // Is accepted type in the list ?
      SPREADCONFIG.LIQ_SOURCES_ACCEPTED_TYPE_VALUES.includes(typeToCheck)
    );
  }

  public isDefaultOrEmptyLiquiditySource(liquiditySourceField: keyof LiquiditySource, value: any): boolean {
    return value === SPREADCONFIG.EMPTY_ROW_DEFAULT[liquiditySourceField] || value === null || value === undefined || value === '';
  }

  public isEmptyDefaultOrBlankLiquiditySource(liquiditySource: LiquiditySource) {
    const isEmptyDefaultOrBlank =
      (liquiditySource && JSON.stringify(liquiditySource) === '{}') ||
      (liquiditySource &&
        this.isDefaultOrEmptyLiquiditySource('name', liquiditySource.name) &&
        this.isDefaultOrEmptyLiquiditySource('mnemonic', liquiditySource.mnemonic) &&
        this.isDefaultOrEmptyLiquiditySource('type', liquiditySource.type) &&
        this.isDefaultOrEmptyLiquiditySource('calculate_group', liquiditySource.calculate_group) &&
        this.isDefaultOrEmptyLiquiditySource('balance_sheet_account_mnemonic', liquiditySource.balance_sheet_account_mnemonic) &&
        this.isDefaultOrEmptyLiquiditySource('balance_sheet_add_factor', liquiditySource.balance_sheet_add_factor) &&
        this.isDefaultOrEmptyLiquiditySource('calculate_group', liquiditySource.calculate_group) &&
        this.isDefaultOrEmptyLiquiditySource('draw_order', liquiditySource.draw_order)) ||
      false;
    
    return isEmptyDefaultOrBlank;
  }

  /*
   * Parses the spreadsheet content, applying auto conversions
   */
  public parseSpreadContentForPeriodEquations(spreadContent: object[]): object[] {
    const columnsConfig = SPREADCONFIG.columnsConfig;
    if (spreadContent && spreadContent.hasOwnProperty('length') && spreadContent.length > 0) {
      spreadContent.forEach((row, index) => {
        for (const key in row) {
          if (row.hasOwnProperty(key)) {
            let value = row[key];
            // Purge values
            // -- 1. Trim all strings and convert null values to empty strings
            value = trimString(value);

            // 2. Convert null and undefined to empty string
            if (!value && value !== 0) {
              value = '';
            }

            // -- 3. Rules by column
            switch (key) {
              // Equation
              case columnsConfig[1].name:
                value = `${value}`; // Makes it string (bug fix 2286)
                break;
            }
            spreadContent[index][key] = value;
          }
        }
      });

      return spreadContent || [];
    }
    return [];
  }

  public parseRowForLiquiditySources(row: LiquiditySource): LiquiditySource {
    const parsing = () => {
      if (JSON.stringify(row) === '{}') {
        row = cloneDeep(SPREADCONFIG.EMPTY_ROW_DEFAULT);
      }

      const columnsConfig = SPREADCONFIG.columnsConfig;
      delete row['user_id'];

      const isDefaultRow = this.isEmptyDefaultOrBlankLiquiditySource(row);

      if ((isDefaultRow && row.source_sid === null) || row.source_sid === undefined) {
        row = new LiquiditySource(row);
      }

      if (!isDefaultRow) {
        for (const key in row) {
          if (row.hasOwnProperty(key)) {
            let value = row[key];

            // Purge values

            // 1. Trim all strings
            value = trimString(value);

            // Rules by column
            switch (key) {
              // NAME
              // --- should be string with some content
              case columnsConfig[0].name:
                value = isDefaultRow ? value : !value || value === '' ? null : value.toString().trim();
                break;

              // MNEMONIC
              case columnsConfig[1].name:
                value = isDefaultRow ? value : !value || value === '' ? null : value.toString().trim();
                break;

              // type
              // --- should be capitalized by default
              // --- If is 1 should be primary, if is 2 secondary, if is 3 tertiary
              case columnsConfig[2].name:
                value =
                  value && Number(value) <= SPREADCONFIG.LIQ_SOURCES_ACCEPTED_TYPE_VALUES.length
                    ? SPREADCONFIG.LIQ_SOURCES_ACCEPTED_TYPE_VALUES[Number(value) >= 0 ? Number(value) - 1 : 0]
                    : value &&
                      typeof value === 'string' &&
                      SPREADCONFIG.LIQ_SOURCES_ACCEPTED_TYPE_VALUES.map(type => type.toLocaleLowerCase()).includes(
                        value.toLocaleLowerCase()
                      )
                    ? capitalizeString(value)
                    : value || null;

                break;

              // calculate_group
              // --- should be an integer number
              // --- blank values should be converted to 0 by default
              // case columnsConfig[3].name:
              //   value = convertBlankTo(value, 0);
              //   break;

              // draw_order
              // --- should be an integer number
              // --- blank values should be converted to 0 by default
              // case columnsConfig[4].name:
              //   value = convertBlankTo(value, null);
              //   break;

              case 'source_sid':
                value = typeof value === 'number' && value > 0 ? value : 0;
                break;
            }
            row[key] = value;
          }
        }
      }

      return row;
    };
    return parsing();
  }

  public serializePeriodEquationsData(spreadContent) {
    // tslint:disable-next-line:quotemark
    const processedData = JSON.stringify(spreadContent).replace(/\'/g, "''");
    return processedData;
  }

  public storeTempPeriodEquations(spreadContent: object[], resourceId: number): Promise<any> {
    return this.eqService.storeTempPeriodEquations('LiquiditySourcesPeriodEquations', spreadContent, resourceId);
  }

  // ===================
  //   Validator rules
  // ====================

  // Should be a number
  public getShouldBeNumberValidator() {
    const dv = GC.Spread.Sheets.DataValidation.createNumberValidator(
      GC.Spread.Sheets.ConditionalFormatting.ComparisonOperators.greaterThanOrEqualsTo,
      0,
      -1,
      true
    );
    dv.showInputMessage(false);
    // dv.inputMessage(`Value must be numeric and greater or equal to 0`);
    // dv.inputTitle(`Tip`);
    return dv;
  }

  // Should be string with a minimum and maximum length (db-limited)
  public getShouldBeStringValidator() {
    const dv = GC.Spread.Sheets.DataValidation.createTextLengthValidator(
      GC.Spread.Sheets.ConditionalFormatting.ComparisonOperators.between,
      '1',
      '50'
    );
    dv.showInputMessage(false);
    // dv.inputMessage(`Value should be a string between 4 and 50 characters`);
    // dv.inputTitle(`Tip`);
    return dv;
  }

  // Should be one of the accepted values included in the list
  public getTypeValidator() {
    const strAcceptedTypes = SPREADCONFIG.LIQ_SOURCES_ACCEPTED_TYPE_VALUES.join(',');
    const dv = GC.Spread.Sheets.DataValidation.createListValidator(strAcceptedTypes);
    dv.showInputMessage(false);
    // dv.inputMessage(`Value should be one of the following: ${strAcceptedTypes}`);
    // dv.inputTitle(`Tip`);
    dv.ignoreBlank(false);
    return dv;
  }

  public getNameValidator(rowIndex: number = 0) {
    // Validation Conditions:
    // -- Default value is allowed
    // -- Should be text
    // -- Cannot be empty
    // -- Cannot contain special characters as wildcards or mathematical operations
    const columnLetter = 'A';
    const sheetRowNum = rowIndex + 1;

    const validationFormulas = this.getValidationFormulas('name')(columnLetter, sheetRowNum);
    const conditionFinalFormula = this.conditionBuilder(validationFormulas, sheetRowNum);

    const validator = GC.Spread.Sheets.DataValidation.createFormulaValidator(conditionFinalFormula);
    validator.ignoreBlank(false);
    return validator;
  }

  public getMnemonicValidator(rowIndex: number = 0) {
    // Validation Conditions:
    //   -- Mnemonic can't be empty
    //   -- Mnemonic should only accept letters, numbers, underscores (_). It should not allow spaces.
    //   -- Mnemonic should be unique in his column
    // ! -- Fix 4781: The mnemonics cannot contain any of the liquidity Types (case-insensitive)

    const columnLetter = 'B';
    const sheetRowNum = rowIndex + 1;

    const validationFormulas = this.getValidationFormulas('mnemonic')(columnLetter, sheetRowNum);
    const conditionFinalFormula = this.conditionBuilder(validationFormulas, sheetRowNum);

    const validator = GC.Spread.Sheets.DataValidation.createFormulaValidator(conditionFinalFormula);
    validator.ignoreBlank(false);
    return validator;
  }

  public getDrawOrderValidator(rowIndex: number = 0) {
    // Validation Conditions:
    // -- Draw Order should be 0 or greater than 0. Numeric and positive.
    // -- Should be unique across the model
    const columnLetter = 'E';
    const sheetRowNum = rowIndex + 1;

    const validationFormulas = this.getValidationFormulas('draw_order')(columnLetter, sheetRowNum);
    const conditionFinalFormula = this.conditionBuilder(validationFormulas, sheetRowNum);
    const validator = GC.Spread.Sheets.DataValidation.createFormulaValidator(conditionFinalFormula);
    validator.ignoreBlank(false);
    return validator;
  }

  public getBalanceSheetAccountListValidator(balanceSheetAccounts: string[]) {
    // Validation Conditions:
    // Should be a valid balance sheet account
    const validator = GC.Spread.Sheets.DataValidation.createListValidator(balanceSheetAccounts.join(','));
    validator.ignoreBlank(true);
    validator.inCellDropdown(false);
    return validator;
  }

  public getAddFactorValidator(rowIndex: number = 0) {
    // Validation Conditions:
    // -- Draw Factor should be a number between 0 and 1000
    const validator = GC.Spread.Sheets.DataValidation.createNumberValidator(
      GC.Spread.Sheets.ConditionalFormatting.ComparisonOperators.between,
      '0',
      '1000',
      false
    );
    validator.ignoreBlank(true);
    return validator;
  }

  public getValidationFormulas(fieldName: keyof LiquiditySource) {
    // Wrapper Functions & helpers
    const isPositiveNumber = (columnLetter, sheetRowNum) => `(
      AND(
        ISNUMBER(${columnLetter}${sheetRowNum}),
        (${columnLetter}${sheetRowNum}>=0)
      )
    )`;

    const isValidNumberInRange = (columnLetter, sheetRowNum, min: number, max: number) => `(
      AND(
        ISNUMBER(${columnLetter}${sheetRowNum}),
        (${columnLetter}${sheetRowNum}>=${min}),
        (${columnLetter}${sheetRowNum}<=${max}),
      )
    )`;

    const noWildcards = (columnLetter, sheetRowNum) => `(
      NOT(OR(
          ISNUMBER(SEARCH("~?",${columnLetter}${sheetRowNum})),
          ISNUMBER(SEARCH("~*",${columnLetter}${sheetRowNum})),
          ISNUMBER(SEARCH("~~",${columnLetter}${sheetRowNum}))
        )
      )
    )`;

    const hasOnlyAcceptedCharacters = (charactersAllowed, columnLetter, sheetRowNum) => `(
      IF(
        TRIM(${columnLetter}${sheetRowNum})="",
        TRUE,
        IF( NOT(ISERROR(SUMPRODUCT(SEARCH(MID(${columnLetter}${sheetRowNum},
          ROW(INDIRECT("1:"&LEN(${columnLetter}${sheetRowNum}))),1),
          "${charactersAllowed}")))),
          TRUE,
          FALSE
        )
      )
    )`;

    const notContainsLiqSourceType = (columnLetter, sheetRowNum) => {
      const conditions = [];
      SPREADCONFIG.LIQ_SOURCES_ACCEPTED_TYPE_VALUES.forEach(type => {
        // CHECK AGAINST EACH LIQ.SRC TYPE CASE INSENSITIVE COMPARISON
        conditions.push(`NOT(TRIM(${columnLetter}${sheetRowNum})="${type}")`);
      });

      const formula = `AND(${conditions.join(', ')})`;
      return formula;
    };

    const isZero = (columnLetter, sheetRowNum) => `${columnLetter}${sheetRowNum} = 0`;

    const isUnique = (columnLetter, sheetRowNum) => `(
      OR(
        (COUNTIF(${columnLetter}:${columnLetter}, ${columnLetter}${sheetRowNum}) = 1)
      )
    )`;

    const notEmpty = (columnLetter, sheetRowNum) => `(
      AND(
        LEN(${columnLetter}${sheetRowNum}) > 0,
        NOT(
          OR(
            ISBLANK(${columnLetter}${sheetRowNum}),
            ${columnLetter}${sheetRowNum}=""
          )
        )
      )
    )`;

    // Functions
    // --- For Name
    const nameFn = (columnLetter, sheetRowNum) => ({
      noWildcards: noWildcards(columnLetter, sheetRowNum),
      nonEmpty: notEmpty(columnLetter, sheetRowNum),
    });

    // --- For Mnemonic
    const mnemonicFn = (columnLetter, sheetRowNum) => ({
      noWildcards: noWildcards(columnLetter, sheetRowNum),
      hasOnlyAcceptedCharacters: hasOnlyAcceptedCharacters(SPREADCONFIG.MNEMONIC_ACCEPTED_CHARACTERS, columnLetter, sheetRowNum),
      nonEmpty: notEmpty(columnLetter, sheetRowNum),
      isUnique: isUnique(columnLetter, sheetRowNum),
      notContainsLiqSourceType: notContainsLiqSourceType(columnLetter, sheetRowNum),
    });

    // --- For draw_order
    const drawOrderFn = (columnLetter: string, sheetRowNum: number) => ({
      noWildcards: noWildcards(columnLetter, sheetRowNum),
      nonEmpty: notEmpty(columnLetter, sheetRowNum),
      isPositiveNumber: isPositiveNumber(columnLetter, sheetRowNum),
      isUnique: isUnique(columnLetter, sheetRowNum),
    });

    const addFactorFn = (columnLetter: string, sheetRowNum: number) => ({
      isValidNumber: isValidNumberInRange(columnLetter, sheetRowNum, 0, 1000),
    });

    // Switcher 3 wild hunt
    switch (fieldName) {
      case 'mnemonic':
        return mnemonicFn;
      case 'name':
        return nameFn;
      case 'draw_order':
        return drawOrderFn;
      case 'balance_sheet_add_factor':
        return addFactorFn;
      default:
        throw new Error(`Unable to return a validation Formula in getValidationFormulas for ${fieldName}`);
    }
  }

  private conditionBuilder(validationFormulas, sheetRow) {
    const isDefaultRow = sheetRowNum => `AND(
      A${sheetRowNum}="",
      B${sheetRowNum}="",
      C${sheetRowNum}="",
      D${sheetRowNum}="",
      E${sheetRowNum}="",
      F${sheetRowNum}="",
      G${sheetRowNum}=""
    )`;

    let conditionFinalFormula = `OR(
      ${isDefaultRow(sheetRow)},
      AND(
    `;

    Object.keys(validationFormulas).forEach((key, i) => {
      // Add concatenation
      if (i > 0 && i < Object.keys(validationFormulas).length) {
        // Each TRUE is interpreted as 1 in a sum. So, the sum of all true values should be equal to the length of the array
        conditionFinalFormula += `,`;
      }
      // Add formula
      conditionFinalFormula += `${validationFormulas[key]}`;
    });

    conditionFinalFormula += `))`;
    return conditionFinalFormula;
  }

  // To check if the sheet is valid
  // --- Generics

  public isValidString(value, minLength: number = 1, maxLength: number = null): boolean {
    if (typeof value === 'string') {
      value = trimString(value);
      return value.length >= minLength && (maxLength === null ? true : value.length <= maxLength);
    }
    return false;
  }

  public isValidNumber(value: any, min: number = -2147483647, max: number = 2147483647): boolean {
    return typeof value === 'number' && value >= min && value <= max;
  }

  public customStringValidator(cellValue: any): boolean {
    if (typeof cellValue === 'string') {
      cellValue = trimString(cellValue);
      return true;
    }
    return false;
  }

  // --- Specifics
  public isValidName(value: any): boolean {
    // const isDefault = value === SPREADCONFIG.EMPTY_ROW_DEFAULT['name'].trim();
    return this.isValidString(value, 2, 50) || false;
  }

  public isValidMnemonic(value: any, spreadContent): boolean {
    // Wrapper auto executed functions
    // ------------------------------
    // const isDefault = value.trim() === SPREADCONFIG.EMPTY_ROW_DEFAULT['mnemonic'].trim();

    const hasOnlyValidCharacters = ((): boolean => {
      // Contains only accepted characters as:
      // --- numbers
      // --- lowerCase or uppercase letters
      // --- underscore (_)
      // consequently it doesn't accepts spaces between. It should be one word
      value = trimString(`${value}`) || '';
      return (value && this.isValidString(value, 1, 100) && value.match(SPREADCONFIG.MNEMONIC_REGEXP)) || false;
    })();

    const isUnique = (() => {
      const mnemonics = spreadContent
        .map(row => (typeof row['mnemonic'] === 'string' ? row['mnemonic'].trim() : row['mnemonic']))
        .filter(elem => elem !== null || elem !== '');
      return mnemonics.filter(elem => elem === value).length === 1;
    })();

    return (typeof value === 'string' && value.trim() === '') || (isUnique && hasOnlyValidCharacters) || false;
  }

  public isValidCalculateGroup(value: any): boolean {
    return this.isValidNumber(value, 0) || false;
  }

  public isValidDrawOrder(value: any): boolean {
    return this.isValidNumber(value, 0) || false;
  }

  public generateSidMap(sources: LiquiditySource[], sourcesOriginal = null): { source_sid: number; mnemonic: string }[] {
    // Reset mapping
    this.sourceIdentifiers = [];

    // Perform mapping
    if (sources) {
      return sources.map((source, index) => {
        const identifier = {
          source_sid: source['source_sid'] || 0,
          mnemonic: source['mnemonic'] || null,
        };

        if (sourcesOriginal && !source['source_sid'] && sourcesOriginal[index]['source_sid']) {
          identifier['source_sid'] = sourcesOriginal[index]['source_sid'] || 0;
        }

        if (identifier.source_sid && identifier.mnemonic) {
          this.sourceIdentifiers.push(identifier);
        }

        return source;
      });
    }
    return null;
  }

  /**
   * Tags / flags an specific source_sid for be inserted, deleted or updated   *
   * @param {*} flagAction
   * @param {number} source_sid
   * @memberof LiquiditySourcesService
   */
  public flagFor(flagAction: 'insert' | 'update' | 'delete', source_sid: number) {
    if (source_sid && typeof source_sid === 'number') {
      switch (flagAction) {
        case 'insert':
          this.setOfSourcesToInsert.add(source_sid);
          break;
        case 'update':
          this.setOfSourcesToUpdate.add(source_sid);
          break;
        case 'delete':
          this.setOfSourcesToDelete.add(source_sid);
          break;
      }
    }
  }

  // Sort Liquidity Sources by type
  public sortLiquiditySourcesByType(sources) {
    return sources.sort((elem1, elem2) => {
      const types = [elem1['type'].toLocaleLowerCase(), elem2['type'].toLocaleLowerCase()];
      return types[0].localeCompare(types[1]);
    });
  }
}
